import { useEffect, useState } from "react";
import { api, type JobOffer } from "../../lib/api";
type FocusedOfferInput = { userId?: number; requestedJobOfferId: string | null; routeThreadId: string | null;
 convo: Awaited<ReturnType<typeof api.getThread>> | null };
export function useFocusedJobOffer({userId,requestedJobOfferId,routeThreadId,convo}: FocusedOfferInput) {
  const [focusedJobOffer, setFocusedJobOffer] = useState<JobOffer | null>(null);
  const [offerLoadError, setOfferLoadError] = useState<string | null>(null);
  const [offerRetry, setOfferRetry] = useState(0);

  // [jobs.offer-recovery](../../../../docs/product/jobs.md#jobs-offer-recovery): a deleted chat keeps messages hidden. The authorized offer read
  // still lets My Applications review the latest terms without restoring them.
  useEffect(() => { setFocusedJobOffer(null); setOfferLoadError(null); }, [requestedJobOfferId, routeThreadId]);
  useEffect(() => {
    if (!userId || !requestedJobOfferId || !routeThreadId || convo?.thread.id !== routeThreadId) return;
    let active = true;
    void api.jobOffer(requestedJobOfferId).then(response => {
      if (!active) return;
      const current = response.currentOffer ?? response.offer;
      if (current.threadId !== routeThreadId) { setOfferLoadError("This offer belongs to another conversation."); return; }
      setFocusedJobOffer(current); setOfferLoadError(null);
    }).catch(() => { if (active) setOfferLoadError("Could not load the latest job offer."); });
    return () => { active = false; };
  }, [userId, requestedJobOfferId, routeThreadId, convo?.thread.id, convo?.messages, offerRetry]);

  const requestedApplicationId = convo?.messages.find(message => message.jobOffer?.id === requestedJobOfferId)?.jobOffer?.applicationId ?? (focusedJobOffer?.threadId === routeThreadId ? focusedJobOffer.applicationId : undefined);
  const focusedJobOfferMessageId = requestedApplicationId ? [...(convo?.messages ?? [])].reverse().find(message => message.jobOffer?.applicationId === requestedApplicationId)?.id : undefined;
  const missingFocusedOffer = !!focusedJobOffer && focusedJobOffer.threadId === convo?.thread.id && !convo?.messages.some(message => message.jobOffer?.applicationId === focusedJobOffer.applicationId && message.jobOffer.version >= focusedJobOffer.version);
  return {focusedJobOffer,offerLoadError,setOfferRetry,focusedJobOfferMessageId,missingFocusedOffer};
}
